import React from 'react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface DateRangeFilterProps {
  value: string;
  onChange: (startDate: string | undefined, endDate: string | undefined, value: string) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const options = [
    { value: 'today', label: t('activities.filters.date_range.today') },
    { value: 'tomorrow', label: t('activities.filters.date_range.tomorrow') },
    { value: 'week', label: t('activities.filters.date_range.week') },
    { value: 'month', label: t('activities.filters.date_range.month') },
    { value: 'all', label: t('activities.filters.date_range.all') },
  ];

  const handleChange = (optionValue: string) => {
    const today = new Date();
    let startDate: string | undefined;
    let endDate: string | undefined;

    switch (optionValue) {
      case 'today':
        startDate = format(startOfDay(today), 'yyyy-MM-dd');
        endDate = format(endOfDay(today), 'yyyy-MM-dd');
        break;
      case 'tomorrow':
        const tomorrow = addDays(today, 1);
        startDate = format(startOfDay(tomorrow), 'yyyy-MM-dd');
        endDate = format(endOfDay(tomorrow), 'yyyy-MM-dd');
        break;
      case 'week':
        startDate = format(startOfWeek(today), 'yyyy-MM-dd');
        endDate = format(endOfWeek(today), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'all':
        startDate = undefined;
        endDate = undefined;
        break;
    }

    onChange(startDate, endDate, optionValue);
  };

  return (
    <div className="inline-flex rounded-md shadow-sm" role="group">
      {options.map((option, index) => (
        <React.Fragment key={option.value}>
          <input
            type="radio"
            className="hidden"
            name="dateRange"
            id={`dateRange-${option.value}`}
            value={option.value}
            checked={value === option.value}
            onChange={() => handleChange(option.value)}
          />
          <label
            htmlFor={`dateRange-${option.value}`}
            className={`px-3 md:px-4 py-3 text-sm font-medium cursor-pointer
              ${index === 0 ? 'rounded-l-lg' : ''}
              ${index === options.length - 1 ? 'rounded-r-lg' : ''}
              ${value === option.value
                ? 'bg-blue-600 text-white'
                : 'bg-bg-secondary text-text-secondary hover:bg-bg-hover'
              }
              border border-border-primary
              ${index === 0 ? 'border-r-0' : ''}
              ${index === options.length - 1 ? 'border-l-0' : 'border-l-0 border-r-0'}
            `}
          >
            {option.label}
          </label>
        </React.Fragment>
      ))}
    </div>
  );
};

export default DateRangeFilter;
